import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SxProps } from '@mui/system';
import React, { Dispatch, SetStateAction } from 'react';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';

interface SortableListItem {
  id: UniqueIdentifier;
}

function DroppableArea(props) {
  const { setNodeRef } = useDroppable({
    id: 'droppable',
  });

  return (
    <Box sx={props.sx}>
      <div ref={setNodeRef}>{props.children}</div>
    </Box>
  );
}

interface DndSortableListItemProps<T extends SortableListItem> {
  children: (
    item: T,
    sortable: ReturnType<typeof useSortable>
  ) => React.ReactNode;
  item: T;
  disabled?: boolean;
  useDragHandle?: boolean;
}

const DndSortableListItem = <T extends SortableListItem>({
  item,
  children,
  disabled = false,
  useDragHandle = false,
}: DndSortableListItemProps<T>) => {
  const sortable = useSortable({
    id: item.id,
    disabled,
  });

  const { attributes, listeners, setNodeRef, transform } = sortable;

  const style = transform
    ? {
        cursor: disabled ? 'default' : useDragHandle ? 'default' : 'grab',
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : { cursor: disabled ? 'default' : useDragHandle ? 'default' : 'grab' };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(disabled || useDragHandle ? {} : listeners)}
      {...(disabled || useDragHandle ? {} : attributes)}
    >
      {children(item, sortable)}
    </div>
  );
};

interface DndSortableListProps<T extends SortableListItem> {
  children: (
    item: T,
    sortable: ReturnType<typeof useSortable>
  ) => React.ReactNode;
  items: T[];
  setItems: Dispatch<SetStateAction<T[]>>;
  sx?: SxProps;
  disabled?: boolean;
  useDragHandle?: boolean;
}

export const DndSortableList = <T extends SortableListItem>({
  children,
  items,
  setItems,
  sx,
  disabled = false,
  useDragHandle = false,
}: DndSortableListProps<T>) => {
  // Hooks must be called unconditionally
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd({ active, over }) {
    if (active?.id && over?.id && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        if (oldIndex !== -1 && newIndex !== -1) {
          return arrayMove(items, oldIndex, newIndex);
        }
        return items;
      });
    }
  }

  // When disabled, simply render the items directly
  if (disabled) {
    return (
      <Box sx={sx}>
        <Stack spacing={1} sx={{ p: 1 }}>
          {items.map((item) => (
            <div key={item.id}>{children(item, null as any)}</div>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
      <DroppableArea sx={sx}>
        <SortableContext
          disabled={disabled}
          items={items}
          strategy={verticalListSortingStrategy}
        >
          <Stack spacing={1} sx={{ p: 1 }}>
            {items.map((item) => (
              <DndSortableListItem
                key={item.id}
                item={item}
                disabled={disabled}
                useDragHandle={useDragHandle}
              >
                {children}
              </DndSortableListItem>
            ))}
          </Stack>
        </SortableContext>
      </DroppableArea>
    </DndContext>
  );
};
